import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DownloadIcon, EyeIcon, PlusIcon, PrinterIcon, SearchIcon } from 'lucide-react';
import { ReceiptModal } from './ReceiptModal';
import { SalesReceiptDocumentAction, SalesReceiptModal } from './SalesReceiptModal';
import { Invoice, Payment, SalesReceipt } from '../../types';
type PaymentFormData = {
  invoiceId: string;
  amount: string;
  method: Payment['method'];
  notes: string;
};
export const Payments: React.FC = () => {
  const {
    payments,
    salesReceipts,
    invoices,
    patients,
    walkInClients,
    addPayment,
    updateInvoice,
    currentUser,
    logBillingAudit
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLedger, setActiveLedger] = useState<'all' | 'invoice' | 'sales'>('all');
  const [showModal, setShowModal] = useState(false);
  const [viewingPayment, setViewingPayment] = useState<Payment | null>(null);
  const [viewingSalesReceipt, setViewingSalesReceipt] = useState<SalesReceipt | null>(null);
  const [salesReceiptAction, setSalesReceiptAction] = useState<SalesReceiptDocumentAction>('view');
  const [formData, setFormData] = useState<PaymentFormData>({
    invoiceId: '',
    amount: '',
    method: 'Cash',
    notes: ''
  });
  const nextReceiptNumber = useMemo(() => {
    const prefix = 'REC-';
    const maxNum = payments.reduce((max, pay) => {
      if (pay.receiptNumber.startsWith(prefix)) {
        const num = parseInt(pay.receiptNumber.slice(prefix.length), 10);
        return !isNaN(num) && num > max ? num : max;
      }
      return max;
    }, 0);
    return `${prefix}${(maxNum + 1).toString().padStart(5, '0')}`;
  }, [payments]);
  const unpaidInvoices = invoices.filter(i => i.status === 'Unpaid' || i.status === 'Partially Paid' || i.status === 'Overdue');
  const filteredPayments = payments.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (p.receiptNumber.toLowerCase().includes(q)) return true;
    const inv = invoices.find(i => i.id === p.invoiceId);
    if (inv && inv.invoiceNumber.toLowerCase().includes(q)) return true;
    return getCustomerName(p.customerId, p.customerType, inv?.customerName).toLowerCase().includes(q);
  });
  const filteredSalesReceipts = salesReceipts.filter(receipt => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return receipt.salesReceiptNumber.toLowerCase().includes(query) || receipt.billTo.toLowerCase().includes(query) || receipt.customerPhone?.toLowerCase().includes(query) || receipt.salespersonName.toLowerCase().includes(query);
  });
  function getCustomerName(customerId: string, type: 'Patient' | 'WalkIn', savedName?: string) {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    }
    return savedName || walkInClients.find(c => c.id === customerId)?.name || 'Walk-in client';
  }
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  const openSalesReceipt = (receipt: SalesReceipt, action: SalesReceiptDocumentAction = 'view') => {
    setSalesReceiptAction(action);
    setViewingSalesReceipt(receipt);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const invoice = invoices.find(i => i.id === formData.invoiceId);
    if (!invoice) {
      alert('Please select a valid invoice.');
      return;
    }
    const amountPaid = parseFloat(formData.amount);
    if (isNaN(amountPaid) || amountPaid <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    if (amountPaid > invoice.balanceDue) {
      alert(`Amount cannot exceed the balance due (${formatCurrency(invoice.balanceDue)}).`);
      return;
    }
    const newPayment = {
      receiptNumber: nextReceiptNumber,
      invoiceId: invoice.id,
      customerId: invoice.customerId,
      customerType: invoice.customerType,
      date: new Date().toISOString(),
      amount: amountPaid,
      method: formData.method,
      cashier: currentUser?.id || '',
      notes: formData.notes,
      createdAt: new Date().toISOString()
    };
    addPayment(newPayment);
    const newAmountPaid = invoice.amountPaid + amountPaid;
    const newBalanceDue = invoice.totalAmount - newAmountPaid;
    let newStatus = invoice.status;
    if (newBalanceDue <= 0) {
      newStatus = 'Paid';
    } else if (newAmountPaid > 0) {
      newStatus = 'Partially Paid';
    }
    updateInvoice(invoice.id, {
      amountPaid: newAmountPaid,
      balanceDue: newBalanceDue,
      status: newStatus
    });
    logBillingAudit('Record Payment', `Recorded payment of ${formatCurrency(amountPaid)} for invoice ${invoice.invoiceNumber}`);
    setShowModal(false);
    setFormData({
      invoiceId: '',
      amount: '',
      method: 'Cash',
      notes: ''
    });
  };
  return <div className="space-y-6" data-id="element-1533">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-1534">
        <div data-id="element-1535">
          <p className="text-sm font-semibold text-pink-600" data-id="element-1536">
            Cashflow workspace
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-950" data-id="element-1537">
            Payments Received
          </h1>
          <p className="mt-1 text-sm text-slate-600" data-id="element-1538">
            Invoice payments and independent cash sales recorded at the billing
            desk.
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} data-id="element-1539">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-1540" /> Record Invoice Payment
        </Button>
      </div>

      <Card noPadding data-id="element-1541">
        <div className="border-b border-gray-200 bg-gray-50 p-4" data-id="element-1542">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1543">
            <div className="flex w-full rounded-md border border-slate-200 bg-white p-1 sm:w-auto" role="tablist" aria-label="Received payment type" data-id="element-1544">
              {[{
              id: 'all',
              label: 'All received'
            }, {
              id: 'invoice',
              label: 'Invoice payments'
            }, {
              id: 'sales',
              label: 'Sales receipts'
            }].map(tab => <button key={tab.id} type="button" role="tab" aria-selected={activeLedger === tab.id} onClick={() => setActiveLedger(tab.id as 'all' | 'invoice' | 'sales')} className={`rounded px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 ${activeLedger === tab.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-1545">
                  {tab.label}
                </button>)}
            </div>
            <div className="relative w-full max-w-md" data-id="element-1546">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-1547" />
              <input type="search" className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm leading-5 text-slate-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="Search receipt, invoice, customer, or phone..." value={searchQuery} onChange={event => setSearchQuery(event.target.value)} aria-label="Search received payments" data-id="element-1548" />
            </div>
          </div>
        </div>

        {(activeLedger === 'all' || activeLedger === 'invoice') && <PaymentLedgerTable payments={filteredPayments} invoices={invoices} getCustomerName={getCustomerName} formatCurrency={formatCurrency} onView={setViewingPayment} data-id="element-1549" />}
        {(activeLedger === 'all' || activeLedger === 'sales') && <SalesReceiptLedgerTable receipts={filteredSalesReceipts} formatCurrency={formatCurrency} onView={openSalesReceipt} data-id="element-1550" />}
      </Card>

      {/* Record Payment Modal */}
      {showModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1551">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-1552">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1553">
              Record Payment
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1554">
              <Select label="Select Invoice" required value={formData.invoiceId} onChange={e => {
            const inv = invoices.find(i => i.id === e.target.value);
            setFormData({
              ...formData,
              invoiceId: e.target.value,
              amount: inv ? inv.balanceDue.toString() : ''
            });
          }} options={[{
            value: '',
            label: '-- Select Unpaid Invoice --'
          }, ...unpaidInvoices.map(i => ({
            value: i.id,
            label: `${i.invoiceNumber} - ${getCustomerName(i.customerId, i.customerType, i.customerName)} (${formatCurrency(i.balanceDue)} due)`
          }))]} data-id="element-1555" />
              <Input label="Amount Received (ZMW)" type="number" step="0.01" required value={formData.amount} onChange={e => setFormData({
            ...formData,
            amount: e.target.value
          })} data-id="element-1556" />
              <Select label="Payment Method" required value={formData.method} onChange={event => setFormData({
            ...formData,
            method: event.target.value as Payment['method']
          })} options={[{
            value: 'Cash',
            label: 'Cash'
          }, {
            value: 'Mobile Money',
            label: 'Mobile Money'
          }, {
            value: 'Debit Card',
            label: 'Debit Card'
          }, {
            value: 'Credit Card',
            label: 'Credit Card'
          }, {
            value: 'Bank Transfer',
            label: 'Bank Transfer'
          }, {
            value: 'Insurance',
            label: 'Insurance'
          }, {
            value: 'Corporate Account',
            label: 'Corporate Account'
          }]} data-id="element-1557" />
              <div className="mt-4" data-id="element-1558">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1559">
                  Notes (Optional)
                </label>
                <textarea className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows={2} value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} placeholder="Transaction ID, Check number, etc." data-id="element-1560" />
              </div>
              <div className="flex justify-end space-x-3 mt-6" data-id="element-1561">
                <Button variant="outline" type="button" onClick={() => setShowModal(false)} data-id="element-1562">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-1563">Save Payment</Button>
              </div>
            </form>
          </div>
        </div>}

      {viewingPayment && <ReceiptModal payment={viewingPayment} onClose={() => setViewingPayment(null)} data-id="element-1564" />}
      {viewingSalesReceipt && <SalesReceiptModal receipt={viewingSalesReceipt} initialAction={salesReceiptAction} onClose={() => setViewingSalesReceipt(null)} data-id="element-1565" />}
    </div>;
};
type PaymentLedgerTableProps = {
  payments: Payment[];
  invoices: Invoice[];
  getCustomerName: (customerId: string, type: 'Patient' | 'WalkIn', savedName?: string) => string;
  formatCurrency: (amount: number) => string;
  onView: (payment: Payment) => void;
};
function PaymentLedgerTable({
  payments,
  invoices,
  getCustomerName,
  formatCurrency,
  onView
}: PaymentLedgerTableProps) {
  return <section aria-label="Invoice payments received" data-id="element-1566">
      <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-3" data-id="element-1567">
        <div data-id="element-1568">
          <h2 className="text-sm font-bold text-slate-900" data-id="element-1569">Invoice payments</h2>
          <p className="mt-0.5 text-xs text-slate-500" data-id="element-1570">
            Payments applied to outstanding invoice balances.
          </p>
        </div>
        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-id="element-1571">
          {payments.length}
        </span>
      </div>
      <div className="overflow-x-auto" data-id="element-1572">
        <table className="min-w-[860px] w-full divide-y divide-gray-200" data-id="element-1573">
          <thead className="bg-gray-50" data-id="element-1574">
            <tr data-id="element-1575">
              {['Date', 'Receipt #', 'Invoice #', 'Customer', 'Method', 'Amount', ''].map(heading => <th key={heading || 'actions'} scope="col" className={`px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-500 ${heading === 'Amount' || !heading ? 'text-right' : 'text-left'}`} data-id="element-1576">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white" data-id="element-1577">
            {payments.length ? [...payments].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(payment => {
            const invoice = invoices.find(item => item.id === payment.invoiceId);
            return <tr key={payment.id} className="hover:bg-slate-50/70" data-id="element-1578">
                      <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600" data-id="element-1579">
                        {new Date(payment.date).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-5 py-3.5 font-mono text-sm font-semibold text-emerald-700" data-id="element-1580">
                        {payment.receiptNumber}
                      </td>
                      <td className="whitespace-nowrap px-5 py-3.5 text-sm text-blue-700" data-id="element-1581">
                        {invoice?.invoiceNumber || '—'}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-slate-900" data-id="element-1582">
                        {getCustomerName(payment.customerId, payment.customerType, invoice?.customerName)}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-1583">
                        {payment.method}
                      </td>
                      <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-slate-950" data-id="element-1584">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-5 py-3.5 text-right" data-id="element-1585">
                        <button type="button" className="rounded p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="View invoice payment receipt" aria-label={`View receipt ${payment.receiptNumber}`} onClick={() => onView(payment)} data-id="element-1586">
                          <EyeIcon className="h-4 w-4" data-id="element-1587" />
                        </button>
                      </td>
                    </tr>;
          }) : <tr data-id="element-1588">
                <td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-1589">
                  No matching invoice payments.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}
type SalesReceiptLedgerTableProps = {
  receipts: SalesReceipt[];
  formatCurrency: (amount: number) => string;
  onView: (receipt: SalesReceipt, action?: SalesReceiptDocumentAction) => void;
};
function SalesReceiptLedgerTable({
  receipts,
  formatCurrency,
  onView
}: SalesReceiptLedgerTableProps) {
  return <section aria-label="Independent sales receipts" className="border-t border-slate-200" data-id="element-1590">
      <div className="flex items-center justify-between border-b border-slate-100 bg-blue-50/50 px-5 py-3" data-id="element-1591">
        <div data-id="element-1592">
          <h2 className="text-sm font-bold text-slate-900" data-id="element-1593">
            Independent sales receipts
          </h2>
          <p className="mt-0.5 text-xs text-slate-500" data-id="element-1594">
            Cash sales — separate from invoice balances.
          </p>
        </div>
        <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700" data-id="element-1595">
          {receipts.length}
        </span>
      </div>
      <div className="overflow-x-auto" data-id="element-1596">
        <table className="min-w-[860px] w-full divide-y divide-gray-200" data-id="element-1597">
          <thead className="bg-gray-50" data-id="element-1598">
            <tr data-id="element-1599">
              {['Visit Date', 'Sales Receipt #', 'Bill to', 'Phone', 'Type', 'Amount', ''].map(heading => <th key={heading || 'actions'} scope="col" className={`px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-500 ${heading === 'Amount' || !heading ? 'text-right' : 'text-left'}`} data-id="element-1600">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white" data-id="element-1601">
            {receipts.length ? [...receipts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(receipt => <tr key={receipt.id} className="hover:bg-slate-50/70" data-id="element-1602">
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600" data-id="element-1603">
                      {formatDate(receipt.visitDate)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 font-mono text-sm font-semibold text-blue-700" data-id="element-1604">
                      {receipt.salesReceiptNumber}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-slate-900" data-id="element-1605">
                      {receipt.billTo}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600" data-id="element-1606">
                      {receipt.customerPhone || '—'}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-1607">
                      <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700" data-id="element-1608">
                        Cash sale
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-slate-950" data-id="element-1609">
                      {formatCurrency(receipt.totalAmount)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-right" data-id="element-1610">
                      <button type="button" className="rounded p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="View sales receipt" aria-label={`View sales receipt ${receipt.salesReceiptNumber}`} onClick={() => onView(receipt)} data-id="element-1611">
                        <EyeIcon className="h-4 w-4" data-id="element-1612" />
                      </button>
                      <button type="button" className="ml-1 rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Print sales receipt" aria-label={`Print sales receipt ${receipt.salesReceiptNumber}`} onClick={() => onView(receipt, 'print')} data-id="element-1613">
                        <PrinterIcon className="h-4 w-4" data-id="element-1614" />
                      </button>
                      <button type="button" className="ml-1 rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Download sales receipt PDF" aria-label={`Download sales receipt ${receipt.salesReceiptNumber} as PDF`} onClick={() => onView(receipt, 'download')} data-id="element-1615">
                        <DownloadIcon className="h-4 w-4" data-id="element-1616" />
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-1617">
                <td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-1618">
                  No matching independent sales receipts.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}
function formatDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}